import { FC, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../store/constants';
import { InvoiceOut, OrderOut } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { mapOrderStatus } from './orderStatusMapper';
import FormatCurrency from '../../shared/utils/formatCurrency';
import { DropDownMenu } from '../../shared/common/DropDownMenu';

type Props = {
    readonly order: OrderOut;
    readonly onDownloadInvoice: (invoice: InvoiceOut) => void;
};

export const OrdersListItem: FC<Props> = ({ order, onDownloadInvoice }) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();
    const orderDateStr = order.orderDate
        ? t('dateFormat.date', {
              val: order.orderDate
          })
        : '';
    const amountStr = t('numberFormat.numberWithCurrencySymbol', {
        val: FormatCurrency.convertIntegerToCurrencyFormat(order.netValue),
        currencySymbol: FormatCurrency.mapCurrencyToSymbol(order.currency)
    });
    const totalEAudiobookCopies = order.totalEAudiobookCopies ? (
        <div className="nowrap">
            {t('shop.orders.totalEAudioCopies', {
                count: order.totalEAudiobookCopies
            })}
        </div>
    ) : null;

    const totalEBookCopies = order.totalEBookCopies ? (
        <div className="nowrap">
            {t('shop.orders.totalEBookCopies', {
                count: order.totalEBookCopies
            })}
        </div>
    ) : null;

    const totalEPressCopies = order.totalEPressCopies ? (
        <div className="nowrap">
            {t('shop.orders.totalEPressCopies', {
                count: order.totalEPressCopies
            })}
        </div>
    ) : null;

    let invoiceLink: ReactNode = '';
    if (order.invoices) {
        if (order.invoices.length > 1) {
            invoiceLink = (
                <DropDownMenu
                    expanded={expanded}
                    onToggleClick={() => setExpanded(!expanded)}
                    align="left"
                    gap={3}
                    onClickOutside={() => setExpanded(false)}
                    label={`\u00A0${t('shop.orders.invoices')}`}
                    labelClassName="txt-small"
                    className="orders-invoice-dd"
                    iconCode="file_download"
                    iconDefaultClasses="material-icons md-upper"
                    arrowClassName="md-upper"
                >
                    {order.invoices.map((invoice, i) => {
                        return (
                            <li key={i}>
                                <a onClick={() => onDownloadInvoice(invoice)}>
                                    <i className="material-icons md-upper">file_download</i>&nbsp;
                                    {invoice.invoiceType}&nbsp;
                                    {invoice.invoiceNumber}&nbsp;({invoice.date})
                                </a>
                            </li>
                        );
                    })}
                </DropDownMenu>
            );
        } else if (order.invoices.length === 1) {
            const invoice = order.invoices[0];
            invoiceLink = (
                <a onClick={() => onDownloadInvoice(invoice)}>
                    <i className="material-icons md-upper">file_download</i>&nbsp;
                    <span className="cold-black">
                        {invoice.invoiceType}&nbsp;{invoice.invoiceNumber}
                    </span>
                </a>
            );
        }
    }

    const account = order.account;

    const translatedStatus = t(`shop.orders.${mapOrderStatus(order.status)}`, order.status ?? '');
    const spocTypeContext = order.spocType?.toLowerCase() === 'site' ? 'individual' : 'shared';
    return (
        <tr>
            <td>
                {order.id}
                <br />
                <span className="cold-paynes">{order.purchaseOrderReference}</span>
            </td>
            <td>{orderDateStr}</td>
            <td className="account-cell">
                <div>{`${account?.type} - ${account?.name}`}</div>
                <div>{account?.code}</div>
            </td>
            <td className="fixed-cell">
                {order.deliveredTo}
                <br />
                <div className="txt-smallest">
                    {t(`shop.orders.collection_${spocTypeContext}`, {
                        defaultValue: ''
                    })}
                </div>
            </td>
            <td>
                {totalEAudiobookCopies}
                {totalEBookCopies}
                {totalEPressCopies}
            </td>
            <td className="total-cell">{amountStr}</td>
            <td className="nowrap">{translatedStatus}</td>
            <td className="fixed-cell">
                <Link to={`${types.PATH_ORDERS}/${order.id}`}>
                    <i className="material-icons md-upper">forward</i>&nbsp;
                    <span className="cold-black">{t('shop.orders.viewDetails')}</span>
                </Link>
                <br />
                {invoiceLink}
            </td>
        </tr>
    );
};
